/**
 * Shared HTTP contract between the web app and the worker API.
 * Both sides import from `@wiseowl/shared` so a rename here is a
 * type error on both sides.
 */

export interface NotificationPrefs {
  dailyNudge: boolean;
  streakNudge: boolean;
  weeklyEmail: boolean;
  insights: boolean;
  productNews: boolean;
  dataImprove: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string; // ISO 8601
  notifications: NotificationPrefs;
}

export interface Session {
  token: string;
  user: User;
  expiresAt: string; // ISO 8601
}

/** Sessions list entry returned to /api/me/sessions; never includes the raw token. */
export interface SessionInfo {
  /** Public sha256(token) — safe to expose; used for revoke-by-id. */
  id: string;
  createdAt: string;
  lastSeenAt: string;
  userAgent: string | null;
  ip: string | null;
  /** True for the session that issued the current request. */
  current: boolean;
}

/* ---------- request payloads ---------- */

export interface SignUpRequest {
  email: string;
  name: string;
  /** Optional — users can sign in with email codes alone. */
  password?: string;
}

/** Request a code for an existing account (email-code sign-in). */
export interface SignInCodeRequest {
  email: string;
}

/** Sign in directly with a previously set password. */
export interface SignInPasswordRequest {
  email: string;
  password: string;
  /** Extend session lifetime to 30 days instead of the 1-day default. */
  rememberMe?: boolean;
}

/** Exchange a 6-digit OTP for a session. */
export interface VerifyRequest {
  email: string;
  code: string;
  rememberMe?: boolean;
}

/** Trigger a password-reset OTP. Always succeeds (doesn't leak existence). */
export interface ForgotPasswordRequest {
  email: string;
}

/** Set / change the password for the signed-in user. */
export interface SetPasswordRequest {
  password: string;
}

/** PATCH /api/me — partial profile updates. */
export interface UpdateProfileRequest {
  displayName?: string;
  notifications?: Partial<NotificationPrefs>;
}

/** POST /api/me/password — change password while signed in. */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/** DELETE /api/me — confirm with email to avoid accidents. */
export interface DeleteAccountRequest {
  confirmation: string;
}

/* ---------- response payloads ---------- */

/**
 * Returned by sign-up / sign-in (code) — a code was issued and is
 * pending verification. While no email provider is configured, the
 * worker also returns `devOtp` so the flow is completable end-to-end
 * (it self-disables the moment EMAIL_PROVIDER_KEY is set).
 */
export interface PendingAuth {
  pending: true;
  email: string;
  /** Present ONLY when the server has no email provider configured. */
  devOtp?: string;
}

/* ---------- practices & moods ---------- */

export type PracticeAxis = "mind" | "body" | "soul";

export interface Practice {
  slug: string;
  name: string;
  eyebrow: string | null;
  heroQuote: string | null;
  expectedMinutes: number;
  axis: PracticeAxis;
  category: string;
  methodology: string | null;
  aiRequired: boolean;
}

export interface PracticeDetail extends Practice {
  /** Parsed journey steps, or null for free-form practices (Companion, Journal). */
  journey: unknown[] | null;
}

export interface Enrollment {
  practiceSlug: string;
  enrolledAt: string;
  lastCompletedAt: string | null;
  totalCompleted: number;
}

export interface MoodEntry {
  id: string;
  moodSlug: string;
  source: string;
  createdAt: string;
}

export interface LogMoodRequest {
  moodSlug: string;
  /** Where the pick came from. Defaults to 'home-row'. */
  source?: string;
}

/* ---------- reflections, insight, streak ---------- */

export interface CreateReflectionRequest {
  practiceSlug: string;
  /** Mood-before (the arrival mood for this reflection). */
  moodSlug?: string;
  body: string;
  /** Somatic body-check zones, e.g. { chest: 3, shoulders: 2 }. */
  bodyZones?: Record<string, number>;
}

export interface ReflectionRef {
  id: string;
  practiceSlug: string;
  moodSlug: string | null;
  createdAt: string;
}

/** RAIN-shaped insight (Recognise / Allow / Investigate / Nurture). */
export interface InsightDto {
  acknowledgement: string;
  toneTags: string[];
  insight: string;
  questions: string[];
  tinyAction: string;
  promptVersion: string;
  modelId: string;
}

export interface CompleteRequest {
  reflectionId?: string;
  /** Mood-after, for the before/after delta. */
  moodAfter?: string;
}

export interface Streak {
  practiceSlug: string;
  currentDays: number;
  longestDays: number;
  lastDoneDate: string | null;
}

export interface CompleteResult {
  streak: Streak;
  /** -1 worse, 0 same, 1 better — when both before & after are known. */
  moodDelta: number | null;
}

/* ---------- library reads ---------- */

export interface ReflectionListItem {
  id: string;
  practiceSlug: string;
  moodSlug: string | null;
  moodAfter: string | null;
  /** First ~140 chars of the decrypted body. */
  preview: string;
  saved: boolean;
  createdAt: string;
}

export interface ReflectionFull {
  id: string;
  practiceSlug: string;
  moodSlug: string | null;
  moodAfter: string | null;
  body: string;
  saved: boolean;
  createdAt: string;
  insight: InsightDto | null;
}

export interface PatternsSummary {
  streak: Streak;
  totalReflections: number;
  /** Mood counts over the last 7 days, most frequent first. */
  moodCounts: { moodSlug: string; count: number }[];
  /** Body-tension tally over the last 7 days. */
  bodyZones: { zone: string; total: number }[];
}

/* ---------- public (anonymous) reflection ---------- */

/** Body for the unauthenticated landing-page insight generator. */
export interface PublicInsightRequest {
  moodSlug: string | null;
  text: string;
}

export interface PublicInsightResponse {
  insight: InsightDto;
}

/* ---------- companion (AI personal chat) ---------- */

export interface CompanionMessage {
  id: string;
  role: "owl" | "me";
  text: string;
  /** Transient UI states / special cards. */
  kind?: "sensing" | "reflection";
  createdAt: string;
}

export interface CompanionThreadRef {
  id: string;
  title: string | null;
  updatedAt: string;
}

export interface CompanionSendRequest {
  threadId?: string;
  text: string;
}

export interface CompanionSendResponse {
  threadId: string;
  reply: CompanionMessage;
  /** Optional RAIN reflection card surfaced alongside the reply. */
  reflection?: InsightDto;
}

/* ---------- daily greeting ---------- */

export interface DailyGreeting {
  /** Personalised, time-aware greeting line, e.g. "Good evening, John." */
  greeting: string;
  /** A warm or mood-aware second line. */
  note: string;
  /** Rotating reflective prompt for today's reset. */
  prompt: string;
  /** Rotating quiet wisdom line. */
  wisdom: string;
}

/* ---------- response envelopes ---------- */

export interface ApiError {
  error: {
    code: string;
    message: string;
    /** Field-level validation errors keyed by JSON path. */
    fields?: Record<string, string>;
  };
}

export type ApiResult<T> = { data: T } | ApiError;

export const isApiError = <T>(r: ApiResult<T>): r is ApiError =>
  "error" in r;
